import React, { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { SearchFilterContentTypes } from '../../../../app/Shared/enums';
import { SearchOption } from '../../../../app/Shared/types';
import useMedia from '../../../../common/hooks/useMedia';
import { searchOrigins } from '../../../Navigation/constants';
import { saveSearchHistory } from '../navSearch/AutosuggestElements/util';
import {
  NavSearchContext,
  NavSearchContextProvider,
  NavSearchProps,
} from '../navSearch/NavSearchContext';
import CrossAssetNavSearchBox from '../navSearch/NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from '../navSearch/NavSearchElements/NavSearchBox';
import { cleanSearchKeywords } from '../navSearch/NavSearchElements/searchUtils';
import ProminentSearchDropdown from '../navSearch/ProminentSearchElements/ProminentSearchDropdown';
import ProminentSearchButton from './ProminentSearchButton';

export interface ProminentSearchProps extends NavSearchProps {
  onDropdownChange: (contentType: string) => void;
  searchTerm: string;
  prominentSearchOptions: SearchOption[];
  contentTypeFromPrevious: SearchFilterContentTypes;
  showDropDown: boolean;
  showContentToggles?: boolean;
}

export default function ProminentSearchNav(props: ProminentSearchProps) {
  const {
    onDropdownChange,
    onSubmit,
    userHasAnySubscription,
    prominentSearchOptions,
    contentTypeFromPrevious,
    showDropDown,
    showContentToggles = false,
    useSnapshots = false,
  } = props;

  const defaultSearchOption =
    prominentSearchOptions?.[0]?.id ?? contentTypeFromPrevious;

  const [contentType, setContentType] = useState(
    defaultSearchOption ?? contentTypeFromPrevious
  );
  const [searchTerm, setSearchTerm] = useState(props.searchTerm ?? '');

  // Ensure searchTerm stays up to date when given a new one via props.
  useEffect(() => {
    setSearchTerm(props.searchTerm ?? '');
  }, [props.searchTerm]);

  useEffect(() => {
    setContentType(contentTypeFromPrevious);
  }, [contentTypeFromPrevious]);

  const isMediumScreen = useMedia('(min-width: 672px)', true);

  const handleDropdownChange = (contentType) => {
    onDropdownChange(contentType);
    setContentType(contentType);
  };

  const makeEmptySuggestions = () => ({
    keywords: [],
    relatedCollections: [],
    latestCollections: [],
    trendingCollections: [],
    relatedContentTypesAndKeywords: [],
  });

  const [suggestions, setSuggestions] = useState(makeEmptySuggestions());

  const clearSuggestions = useCallback(
    () => setSuggestions(makeEmptySuggestions()),
    []
  );
  const handleSubmit = (
    contentTypeSearched: SearchFilterContentTypes = contentType,
    value: string = searchTerm,
    searchOriginOverride: string = ''
  ) => {
    const cleanedSearchKeywords = cleanSearchKeywords(value);
    const searchOrigin = searchOriginOverride || searchOrigins.SEARCH_BAR;

    if (props?.enableSearchHistory) {
      saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
    }
    onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
  };

  const activeTab = 'bg-white text-gray-900 thick-3 offset-5';
  const inactiveTab = 'text-white bg-gray-900 bg-opacity-20';

  return (
    <NavSearchContextProvider {...props}>
      <NavSearchContext.Consumer>
        {({ setShowSuggestions }) => (
          <div
            className={twMerge(
              'flex flex-col w-full font-inter text-gray-900 items-center',
              showContentToggles ? 'pb-8' : ''
            )}
          >
            {showContentToggles && (
              <div className="product m-0 flex text-base font-bold grid-rows-4 pb-4 md:pb-6 lg:pb-6 cursor-pointer gap-3">
                {prominentSearchOptions.map((link) => (
                  <div
                    role="button"
                    tabIndex={0}
                    key={link.id}
                    className={twMerge(
                      'text-base rounded-lg px-2 py-1 border-0 leading-snug',
                      contentType === link.id ? activeTab : inactiveTab
                    )}
                    data-value={link.id}
                    onClick={() => handleDropdownChange(link.id)}
                    onKeyDown={() => handleDropdownChange(link.id)}
                  >
                    {link.label}
                  </div>
                ))}
              </div>
            )}
            <form
              className="border-none rounded-full lg:w-822px md:w-480px pl-5 flex items-center bg-white"
              onSubmit={(event) => {
                event.preventDefault();
                setShowSuggestions(false);
                handleSubmit(contentType, searchTerm);
              }}
            >
              {showDropDown && (
                <>
                  <ProminentSearchDropdown
                    onChange={handleDropdownChange}
                    value={contentType}
                    useSnapshots={useSnapshots}
                    clearSuggestions={clearSuggestions}
                  />
                  <div>
                    <div className="w-px h-11 bg-gray-500" />
                  </div>
                </>
              )}
              {isMediumScreen ? (
                <CrossAssetNavSearchBox
                  contentType={contentType}
                  setSearchTerm={setSearchTerm}
                  onSubmit={handleSubmit}
                  searchTerm={searchTerm}
                  suggestions={suggestions}
                  setSuggestions={setSuggestions}
                  clearSuggestions={clearSuggestions}
                  placeholder={
                    prominentSearchOptions.find(({ id }) => id === contentType)
                      ?.placeholderText
                  }
                />
              ) : (
                <NavSearchBox
                  contentType={contentType}
                  onChange={setSearchTerm}
                  onSubmit={handleSubmit}
                  searchTerm={searchTerm}
                  placeholder={
                    prominentSearchOptions.find(({ id }) => id === contentType)
                      ?.placeholderText
                  }
                  userHasAnySubscription={userHasAnySubscription}
                  isProminentSearch
                />
              )}
              <ProminentSearchButton />
            </form>
          </div>
        )}
      </NavSearchContext.Consumer>
    </NavSearchContextProvider>
  );
}
